/* Hydrogen-reference interactions. The rest of the theme has no dependency on this module. */
import EmblaCarousel from './ol-embla.esm.js';

class ReferenceCarousel extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.viewport = this.querySelector('.hf-viewport');
    if (!this.viewport?.firstElementChild?.children.length) return;
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.paused = false;
    this.inView = false;
    this.pointerActive = false;
    this.hovered = false;
    try {
      this.api = EmblaCarousel(this.viewport, {
        align: 'start', loop: this.dataset.loop === 'true', dragFree: false,
        duration: this.motion.matches ? 0 : 25
      });
      this.setAttribute('data-carousel-ready', '');
    } catch {
      // No content, customer text, URLs or identifiers are logged.
      console.warn('[OudLibrary]', {level: 'warning', feature: 'reference-carousel', code: 'native-scroll-fallback'});
      return;
    }
    this.api.on('pointerDown', () => { this.pointerActive = true; this.schedule(); });
    this.api.on('pointerUp', () => { this.pointerActive = false; this.schedule(); });
    this.api.on('reInit', () => this.schedule());
    this.viewport.addEventListener('keydown', event => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'ArrowLeft') this.api.scrollPrev(this.motion.matches);
      if (event.key === 'ArrowRight') this.api.scrollNext(this.motion.matches);
      if (event.key === 'Home') this.api.scrollTo(0, this.motion.matches);
      if (event.key === 'End') this.api.scrollTo(this.api.scrollSnapList().length - 1, this.motion.matches);
    }, {signal});
    this.addEventListener('mouseenter', () => { this.hovered = true; this.schedule(); }, {signal});
    this.addEventListener('mouseleave', () => { this.hovered = false; this.schedule(); }, {signal});
    this.addEventListener('focusin', () => this.schedule(), {signal});
    this.addEventListener('focusout', () => queueMicrotask(() => this.schedule()), {signal});
    this.querySelector('[data-pause]')?.addEventListener('click', event => {
      this.paused = !this.paused;
      event.currentTarget.textContent = this.paused ? 'Resume reviews' : 'Pause reviews';
      this.schedule();
    }, {signal});
    this.motion.addEventListener('change', () => {
      this.api.reInit({duration: this.motion.matches ? 0 : 25});
      this.schedule();
    }, {signal});
    document.addEventListener('visibilitychange', () => this.schedule(), {signal});
    // Block selection must expose its slide without fighting the editor.
    this.addEventListener('shopify:block:select', event => {
      const index = this.api.slideNodes().findIndex(node => node === event.target || node.contains(event.target));
      if (index >= 0) this.api.scrollTo(index, true);
    }, {signal});
    this.observer = new IntersectionObserver(entries => {
      this.inView = entries[0].isIntersecting;
      this.schedule();
    });
    this.observer.observe(this);
  }

  schedule() {
    clearTimeout(this.timer);
    if (!this.isConnected || !this.api || this.dataset.autoplay !== 'true' || this.paused ||
        this.motion.matches || !this.inView || this.pointerActive || this.hovered ||
        this.contains(document.activeElement) || document.hidden || window.Shopify?.designMode ||
        this.api.scrollSnapList().length < 2) return;
    this.timer = setTimeout(() => { this.api.scrollNext(); this.schedule(); }, 5000);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.observer?.disconnect();
    this.controller?.abort();
    this.api?.destroy();
    this.controller = null;
    this.api = null;
    this.removeAttribute('data-carousel-ready');
  }
}


if (!customElements.get('ol-reference-carousel')) customElements.define('ol-reference-carousel', ReferenceCarousel);
